
import {
  defineComponent,
  ref,
  computed,
  nextTick,
} from 'vue';
import BaseDropdown from '@/components/fields/partials/BaseDropdown.vue';
import BaseTextInput from '@/components/fields/BaseTextInput.vue';
import BaseInputNumber from '@/components/fields/partials/BaseInputNumber.vue';
import BaseCalendar from '@/components/fields/BaseCalendar.vue';
import { useI18n } from 'vue-i18n';
import DataTable, { DataTableRowReorderEvent } from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Message from 'primevue/message';
import AutoComplete from 'primevue/autocomplete';
import Chip from 'primevue/chip';
import ConfirmDialog from 'primevue/confirmdialog';
import Checkbox from 'primevue/checkbox';
import Tooltip from 'primevue/tooltip';
import { showToastError } from '@/helpers';
import { validateFieldWithVuelidate } from '@/composables/custom-data/customData.validations';
import {
  Field, AvailableTypeEnum, UNIQUE_KEY_ELIGIBLE_TYPES,
  ForeignKeyRow,
} from '@/composables/custom-data/customData.interfaces';
import { useStore } from '@/store';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmPopup from 'primevue/confirmpopup';
import { OVERRIDABLE_FIELDS } from '@/composables/custom-data/overridableSystemFields';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import SpmPanelMenu from '@/components/spm-primevue/SpmPanelMenu.vue';

export default defineComponent({
  name: 'SchemaFieldsEditor',

  components: {
    DataTable,
    Column,
    Button,
    Message,
    AutoComplete,
    ConfirmDialog,
    Chip,
    BaseDropdown,
    BaseTextInput,
    BaseInputNumber,
    BaseCalendar,
    Checkbox,
    ConfirmPopup,
    SpmOverlayPanel,
    SpmPanelMenu,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();
    const store = useStore();
    const confirm = useConfirm();
    const idCustomDataDefinition = computed(() => store.getters['customObject/getIdCustomDataDefinition']);
    const fields = computed(() => store.getters['customObject/getFields']);
    const relationships = computed(() => store.getters['customObject/getRelationships']);
    const isEditMode = computed(() => store.getters['customObject/isEditMode']);
    const currentEditingElement = computed(() => store.getters['customObject/getCurrentEditingElement']);
    const refreshViewComponent = computed(() => store.getters['customObject/getRefreshViewComponent']);
    const overrides = computed(() => store.getters['customObject/getOverrides']);
    const existingFields = computed(() => store.getters['customObject/getNeededDataExistingFields'] || []);
    const descriptionOpenId = ref<number | null>(null);
    const closeActionsPanel = ref(false);

    // check if the field is editable
    const isFieldEditable = (field: Field): boolean => !existingFields.value.some((f: any) => f.id === field.id);
    // check if the field is on edit
    const fieldOnEdit = (field: Field): boolean => currentEditingElement.value
     && currentEditingElement.value.id === field.id
      && currentEditingElement.value.elementType === 'field';
    // check if the field is a new add field
    const isNewAddField = (field: Field): boolean => currentEditingElement.value
     && currentEditingElement.value.id === field.id
      && currentEditingElement.value.elementType === 'field'
       && currentEditingElement.value.isNew;

    const typeAvailable = ref(
      Object.values(AvailableTypeEnum).map((type) => ({
        label: t(`customData.types.${type}`),
        value: type,
      })),
    );

    // helper functions
    const cloneField = (field: Field): Field => JSON.parse(JSON.stringify(field));
    const stripDiacritics = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const labelToName = (label: string) => stripDiacritics(label)
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/^_+|_+$/g, '')
      .substring(0, 50);

    // Auto-generate name from label (only for new fields)
    const onLabelInput = (field: Field) => {
      if (isNewAddField(field)) {
        // eslint-disable-next-line no-param-reassign
        field.name = labelToName(field.label);
      }
    };

    // Toggle description textarea visibility
    const toggleDescription = (id: number) => {
      descriptionOpenId.value = descriptionOpenId.value === id ? null : id;
    };

    /**
     * Basic field functions
     */
    // check if the field is eligible for unique key
    const isUniqueKeyEligible = (fieldRowData: Field): boolean => UNIQUE_KEY_ELIGIBLE_TYPES.includes(fieldRowData.type);

    // check if the field is eligible for relation (must be text or number)
    const isFieldEligibleForRelation = (fieldRowData: Field): boolean => ['text', 'number'].includes(fieldRowData.type);

    // check if the field has a relation
    const hasRelation = (fieldRowData: Field): boolean => relationships.value.some(
      (rel: { localField: string; isEditing: any }) => rel.localField === fieldRowData.name && !rel.isEditing,
    );

    // check if the field is used in an override
    const isFieldUsedInOverride = (fieldRowData: Field): boolean => overrides.value.some((o: any) => o.custom_field_name === fieldRowData.name
     && parseInt(o.id_custom_data_definition, 10) === parseInt(idCustomDataDefinition.value, 10));

    /**
     * Basic DataTables functions
     */
    // get the class of the row depending on the state of the field (isEditing)
    const getRowClass = (fieldRowData: Field): string | undefined => (fieldOnEdit(fieldRowData) ? 'row-is-editing' : undefined);

    /**
     * Actions on fields functions (add, remove, edit, cancel edit, save, update) on the store
     */

    // add a new empty field
    const addNewEmptyField = () => {
      if (currentEditingElement.value !== null) {
        showToastError(t('customData.fieldsEditor.actions.addNewEmpty'), 4000);
        return;
      }
      store.dispatch('customObject/handleFieldAction', { action: 'add-new-empty' });
    };

    // remove a field
    const removeField = (event: any, field: Field) => {
      confirm.require({
        group: 'basicGroup',
        target: event.currentTarget,
        header: t('confirmation'),
        message: t('customData.fieldsEditor.actions.deleteField.confirmMessage'),
        icon: 'fas fa-exclamation-triangle',
        acceptLabel: t('yes'),
        rejectLabel: t('no'),
        acceptClass: 'p-button-success',
        rejectClass: 'p-button-secondary',
        accept: () => {
          // verify if the field is used in a relation
          const usedInRelation = relationships.value.find((rel: ForeignKeyRow) => rel.localField === field.name);
          // verify if the field is used in an override
          const usedInOverride = overrides.value.find((o: any) => o.custom_field_name === field.name && parseInt(o.id_custom_data_definition, 10) === parseInt(idCustomDataDefinition.value, 10));

          if (usedInRelation || usedInOverride) {
            confirm.require({
              header: t('confirmation'),
              message: t('customData.fieldsEditor.actions.deleteField.confirmMessage2'),
              icon: 'fas fa-exclamation-triangle',
              acceptLabel: t('yes'),
              rejectLabel: t('no'),
              acceptClass: 'p-button-success',
              rejectClass: 'p-button-secondary',
              accept: () => {
                store.dispatch('customObject/handleFieldAction', { action: 'remove', id: field.id });
                nextTick(() => { closeActionsPanel.value = true; });
              },
              reject: () => { nextTick(() => { closeActionsPanel.value = true; }); },
            });
          } else {
            store.dispatch('customObject/handleFieldAction', { action: 'remove', id: field.id });
            nextTick(() => { closeActionsPanel.value = true; });
          }
        },
        reject: () => { nextTick(() => { closeActionsPanel.value = true; }); },
      });
    };

    // start editing a field
    const editField = (id: number) => {
      if (currentEditingElement.value !== null) {
        showToastError(t('customData.fieldsEditor.actions.editField'), 4000);
        return;
      }
      store.dispatch('customObject/handleFieldAction', { action: 'edit', id });
    };

    // cancel editing a field
    const cancelEditField = (id: number) => store.dispatch('customObject/handleFieldAction', { action: 'cancel-edit', id });

    // update a field when dynamicly changing the field data
    const updateField = (id: number, fieldCloned: Field) => {
      const fieldIndex = fields.value.findIndex((field: Field) => field.id === id);
      if (fieldIndex !== -1) {
        store.dispatch('customObject/handleFieldAction', { action: 'instant-update-data', id, fieldData: { ...fieldCloned } });
      }
    };

    const isFieldCompatibleWithOverride = (field: Field, override: any): boolean => {
      // Check if the field is not a unique key
      if (field.unique_key) return false;
      // Check if the field is not used in a relation
      const isUsedInRelation = relationships.value.some((rel: ForeignKeyRow) => rel.localField === field.name);
      if (isUsedInRelation) return false;
      // Check if the type of the field is compatible with the system field
      const targetSchema = override.target_system_schema;
      const overridableFields = OVERRIDABLE_FIELDS[targetSchema as keyof typeof OVERRIDABLE_FIELDS] || [];
      const systemField = overridableFields.find((sf: any) => sf.field === override.system_field_name);

      if (!systemField) return false;
      // The type of the custom field must correspond to the type of the system field (if not, the override will be deleted)
      const systemFieldTypes = Array.isArray(systemField.type) ? systemField.type : [systemField.type];
      return systemFieldTypes.includes(field.type as string);
    };

    // save a field
    const saveField = (id: number) => {
      const fieldIndex = fields.value.findIndex((field: Field) => field.id === id);
      if (fieldIndex === -1) {
        return;
      }

      const errorsFields = [];
      const currentField = fields.value[fieldIndex];
      // Gather names of other fields to check for uniqueness
      const otherNames = fields.value.filter((f: Field, idx: number) => idx !== fieldIndex).map((f: Field) => f.name);
      // Validate the current field with Vuelidate
      const validationResult = validateFieldWithVuelidate(t, currentField, otherNames);
      if (!validationResult.valid) {
        // Store errors and display them as toast notifications
        Object.entries(validationResult.errors).forEach(([fieldKey, messages]) => {
          errorsFields.push(fieldKey);
          messages.forEach((msg: string) => {
            showToastError(msg, 6000);
          });
        });
        return;
      }

      // Check for geolocation name collisions
      const otherFields = fields.value.filter((f: Field, idx: number) => idx !== fieldIndex);
      if (currentField.type === AvailableTypeEnum.GEOLOCATION) {
        const conflicting = otherFields.find((f: Field) => f.name === `${currentField.name}_lat` || f.name === `${currentField.name}_lng`);
        if (conflicting) {
          showToastError(t('customData.validation.geolocation_name_collision', [currentField.name, conflicting.name]), 6000);
          return;
        }
      } else if (currentField.name.endsWith('_lat') || currentField.name.endsWith('_lng')) {
        const baseName = currentField.name.replace(/_(lat|lng)$/, '');
        const geoField = otherFields.find((f: Field) => f.name === baseName && f.type === AvailableTypeEnum.GEOLOCATION);
        if (geoField) {
          showToastError(t('customData.validation.geolocation_name_collision_reverse', [currentField.name, baseName]), 6000);
          return;
        }
      }

      // check if the field is in relation and if the new properties are compatible
      if (hasRelation(currentEditingElement.value)) {
        const isTypeCompatible = isFieldEligibleForRelation(currentField);

        if (!isTypeCompatible) {
          const issuesText = t('customData.fieldsEditor.actions.saveField.compatibility.type', [currentField.type]);
          confirm.require({
            header: t('customData.fieldsEditor.confirm.relationConflictHeader'),
            message: t('customData.fieldsEditor.confirm.relationConflictMessage', [currentField.name, issuesText]),
            icon: 'fas fa-exclamation-triangle',
            acceptLabel: t('customData.fieldsEditor.confirm.deleteRelationAndSave'),
            rejectLabel: t('customData.fieldsEditor.confirm.cancelModifications'),
            acceptClass: 'p-button-danger',
            // Si l'utilisateur accepte, supprimer la relation et sauvegarder
            accept: () => {
              // get the relation id
              const relationIdToDelete = relationships.value.find((rel: ForeignKeyRow) => rel.localField === currentEditingElement.value.name)?.id;
              if (relationIdToDelete) {
                store.dispatch('customObject/handleFieldAction', { action: 'save', id, fieldData: { ...currentField } });
                store.dispatch('customObject/handleRelationAction', { action: 'remove', id: relationIdToDelete });
              }
            },
            reject: () => {
              store.dispatch('customObject/handleFieldAction', { action: 'cancel-edit', id });
            },
          });
        } else {
          store.dispatch('customObject/handleFieldAction', { action: 'save', id, fieldData: { ...currentField } });
        }
      } else if (isFieldUsedInOverride(currentEditingElement.value)) {
        const usedOverride = overrides.value.find((o: any) => o.custom_field_name === currentEditingElement.value.name
         && parseInt(o.id_custom_data_definition, 10) === parseInt(idCustomDataDefinition.value, 10));
        if (usedOverride && !isFieldCompatibleWithOverride(currentField, usedOverride)) {
          confirm.require({
            header: t('customData.fieldsEditor.confirm.overrideConflictHeader'),
            message: t('customData.fieldsEditor.confirm.overrideConflictMessage', [currentEditingElement.value.name]),
            icon: 'fas fa-exclamation-triangle',
            acceptLabel: t('customData.fieldsEditor.confirm.deleteOverrideAndSave'),
            rejectLabel: t('customData.fieldsEditor.confirm.cancelModifications'),
            acceptClass: 'p-button-success',
            accept: () => {
              store.dispatch('customObject/handleFieldAction', { action: 'save', id, fieldData: { ...currentField } });
              store.dispatch('customObject/handleOverrideAction', { action: 'remove', id: usedOverride.id });
            },
            reject: () => {
              store.dispatch('customObject/handleFieldAction', { action: 'cancel-edit', id });
            },
          });
        } else {
          // if new field name is different from the old one, update the override
          if (currentField.name !== currentEditingElement.value.name) {
            store.dispatch('customObject/handleOverrideAction', { action: 'update', id: usedOverride.id, overrideItem: { ...usedOverride, custom_field_name: currentField.name } });
          }
          store.dispatch('customObject/handleFieldAction', { action: 'save', id, fieldData: { ...currentField } });
        }
      } else {
        // If validation passes and no relation conflicts, emit the save action
        store.dispatch('customObject/handleFieldAction', { action: 'save', id, fieldData: { ...currentField } });
      }
    };

    /**
     * Advanced field functions
     */
    const optionSuggestions = ref<string[]>([]);

    // function to handle when the type of a field is changed
    const handleFieldTypeChange = (field: Field) => {
      const fieldCloned = cloneField(field);
      if (!fieldCloned) return;

      // remove unique_key if the new type is not eligible
      if (!isUniqueKeyEligible(fieldCloned) && fieldCloned.unique_key) {
        fieldCloned.unique_key = false;
      }

      // if the new type is 'list', ensure options is an array
      if (fieldCloned.type === AvailableTypeEnum.LIST) {
        if (!Array.isArray(field.options)) {
          fieldCloned.options = [];
        }
      } else {
        if (fieldCloned.options) {
          delete fieldCloned.options;
        }
      }

      // always reset the default value when type changes
      fieldCloned.default = null;

      // Geolocation fields: force unique_key=false (not indexable as unique)
      if (fieldCloned.type === AvailableTypeEnum.GEOLOCATION) {
        fieldCloned.unique_key = false;
      }

      // trigger the update for this field on the store with the new field data
      updateField(field.id, fieldCloned);
    };

    // add a new option to the field when pressing Enter (only for list type fields / auto complete)
    const addNewOptionToField = (event: KeyboardEvent, field: Field) => {
      const inputElement = event.target as HTMLInputElement;
      const value = inputElement.value.trim();
      const fieldCloned = cloneField(field);

      if (value && fieldCloned) {
        // if the field has no options, initialize an empty array
        if (!Array.isArray(fieldCloned.options)) {
          fieldCloned.options = [];
        }
        // if the option is not already in the field, add it
        if (!fieldCloned.options.some((opt: string) => opt.toLowerCase() === value.toLowerCase())) {
          fieldCloned.options = [...fieldCloned.options, value];
          updateField(field.id, fieldCloned);
          optionSuggestions.value = [];
          event.preventDefault();
          window.requestAnimationFrame(() => {
            if (inputElement) inputElement.value = '';
          });
        } else {
          // if the option is already in the field, prevent adding it and clear the input
          event.preventDefault();
          window.requestAnimationFrame(() => {
            if (inputElement) inputElement.value = '';
          });
        }
      }
    };

    // remove duplicate options when an option is selected (only for list type fields / auto complete)
    const removeDuplicateOption = (event: { value: string }, field: Field) => {
      const fieldCloned = cloneField(field);
      if (!fieldCloned || !Array.isArray(fieldCloned.options)) return;

      const { value: selectedValue } = event;
      const { options } = fieldCloned;

      // Find duplicate (ignoring the last added item)
      const duplicateIndex = options.findIndex(
        (opt: string, index: number) => index !== options.length - 1
          && opt.toLowerCase() === selectedValue.toLowerCase(),
      );

      // Remove the last entry if duplicate found
      if (duplicateIndex !== -1) {
        fieldCloned.options = options.slice(0, -1);
        updateField(field.id, fieldCloned);
      }
    };

    const onFieldOrderChange = (event: DataTableRowReorderEvent) => {
      // if not in edit mode, update the order in the store without checking for existing fields
      if (!isEditMode.value) {
        store.commit('customObject/setFields', event.value);
        return;
      }

      // if in edit mode, don't allow to move fields that already exist in the custom data definition
      const oldOrder = fields.value.map((f: Field) => f.id);
      const newOrder = event.value.map((f: Field) => f.id);

      // Get existing fields from store and filter current fields accordingly
      const existingIds = existingFields.value.map((f: any) => f.id);
      let existingMoved = false;
      for (let i = 0; i < existingIds.length; i++) {
        if (oldOrder.indexOf(existingIds[i]) !== newOrder.indexOf(existingIds[i])) {
          existingMoved = true;
          break;
        }
      }
      if (!existingMoved) {
        // update the order in the store
        store.commit('customObject/setFields', event.value);
      }
    };

    // handle the change of the unique key of a field
    const onUniqueKeyChange = (field: Field) => {
      const fieldCloned = cloneField(field);

      // if the field is a unique key, set the required field to true (unique key fields are always required)
      if (fieldCloned.unique_key) {
        fieldCloned.required = true;
      }
      updateField(field.id, fieldCloned);
    };

    // handle the change of the required field of a field
    const onRequiredChange = (field: Field) => {
      const fieldCloned = cloneField(field);
      // if new required value is false, set the unique key field to false
      if (!fieldCloned.required) {
        fieldCloned.unique_key = false;
      }
      updateField(field.id, fieldCloned);
    };

    const formatListOptionsTooltip = (options: string[]): string => options.join(', ');

    // Close the actions panel after executing an action
    const closeAndRun = (fn: (...args: any[]) => void) => (...args: any[]) => {
      fn(...args);
      nextTick(() => { closeActionsPanel.value = true; });
    };

    // Build menu items for field actions
    const getFieldActions = (field: Field) => {
      if (fieldOnEdit(field)) {
        const items: any[] = [
          {
            label: t('customData.fieldsEditor.tooltips.save'),
            icon: 'fa-solid fa-save',
            callback: closeAndRun(() => saveField(field.id)),
          },
        ];
        if (!isNewAddField(field)) {
          items.push({
            label: t('customData.fieldsEditor.tooltips.cancel'),
            icon: 'fa-solid fa-times',
            callback: closeAndRun(() => cancelEditField(field.id)),
          });
        }
        items.push({ separator: true });
        items.push({
          label: t('customData.fieldsEditor.tooltips.delete'),
          icon: 'fa-regular fa-trash-alt',
          class: 'danger',
          callback: (_data: any, event: any) => removeField(event, field),
        });
        return items;
      }

      if (isFieldEditable(field)) {
        return [
          {
            label: t('customData.fieldsEditor.tooltips.edit'),
            icon: 'fa-solid fa-pencil-alt',
            callback: closeAndRun(() => editField(field.id)),
          },
          { separator: true },
          {
            label: t('customData.fieldsEditor.tooltips.delete'),
            icon: 'fa-regular fa-trash-alt',
            class: 'danger',
            callback: (_data: any, event: any) => removeField(event, field),
          },
        ];
      }

      return [];
    };

    return {
      t,
      refreshViewComponent,
      fields,
      isFieldEditable,
      fieldOnEdit,
      isNewAddField,
      getRowClass,
      isFieldEligibleForRelation,
      isUniqueKeyEligible,
      hasRelation,
      onLabelInput,
      descriptionOpenId,
      toggleDescription,
      // store actions on fields and field functions
      addNewEmptyField,
      removeField,
      editField,
      cancelEditField,
      saveField,
      handleFieldTypeChange,
      optionSuggestions,
      addNewOptionToField,
      removeDuplicateOption,
      onFieldOrderChange,
      onUniqueKeyChange,
      onRequiredChange,
      formatListOptionsTooltip,
      getFieldActions,
      closeActionsPanel,
      // other
      AvailableTypeEnum,
      typeAvailable,
    };
  },
});
